"use client";

import {
  Box,
  Stack,
  styled,
  useTheme,
  Typography,
  Divider,
  Button as MuiButton,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import useWindowSize from "../../hooks/useWindowSize";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import {
  Upload,
  Check,
  AlertCircle,
  X,
  Plus,
  Image,
  Pencil,
} from "lucide-react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import Iconify from "../iconify/iconify";

const StyledMinImgBox = styled(Box)(({ theme }) => ({
  height: "500px",
  paddingRight: "50px",
  borderRadius: "10px",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    height: "220px",
    paddingRight: "0px",
  },
}));

const StyledIndexBox = styled(Box)(({ theme }) => ({
  width: "15px",
  height: "5px",
  borderRadius: "50px",
  cursor: "pointer",
  background: theme.palette.background.light,
  "&.active": {
    background: "#a95052",
  },
}));

const StyledArrowBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  padding: "10px",
  border: `1px solid ${theme.palette.text.secondary}`,
  color: theme.palette.text.secondary,
  cursor: "pointer",
}));

const UploadButton = styled(MuiButton)(({ theme }) => ({
  position: "absolute",
  bottom: "10px",
  right: "10px",
  zIndex: 10,
  backgroundColor: "#a95052",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#8a3f41",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: "6px 12px",
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "10px",
  right: "10px",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  zIndex: 10,
  "&:hover": {
    backgroundColor: "rgba(169, 80, 82, 0.8)",
    transform: "scale(1.1)",
  },
}));

const EditButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "10px",
  right: "50px",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  zIndex: 10,
  "&:hover": {
    backgroundColor: "rgba(169, 80, 82, 0.8)",
    transform: "scale(1.1)",
  },
}));

const AddModelButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: "#a95052",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#8a3f41",
  },
  marginTop: "20px",
}));

const StatusMessage = styled(Box)(({ theme, status }) => ({
  position: "absolute",
  bottom: "60px",
  right: "10px",
  zIndex: 10,
  padding: "8px 12px",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor:
    status === "success"
      ? "rgba(46, 125, 50, 0.9)"
      : status === "error"
      ? "rgba(211, 47, 47, 0.9)"
      : "rgba(0, 0, 0, 0.7)",
  color: "#fff",
  fontSize: "0.875rem",
  maxWidth: "calc(100% - 20px)",
  boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
}));

const DropZone = styled(Box)(({ theme, isDragging }) => ({
  height: "220px",
  width: "100%",
  borderRadius: "10px",
  border: `2px dashed ${
    isDragging ? theme.palette.primary.main : theme.palette.text.secondary
  }`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isDragging ? "rgba(0, 0, 0, 0.05)" : "transparent",
  transition: "all 0.3s ease",
  cursor: "pointer",
  marginBottom: "10px",
}));

// Generate a UUID
const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const ModelsCarouselEdit = ({
  initialModels = [],
  onModelsChange,
  onUpload,
}) => {
  const theme = useTheme();
  const screenWidth = useWindowSize();
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modelsData, setModelsData] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [statusMessage, setStatusMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogEdit, setOpenDialogEdit] = useState(false);

  const [newModel, setNewModel] = useState({
    id: generateUUID(),
    title: "",
    name: "",
    surface: "",
    properties: [""],
    images: [],
    isNew: true,
  });

  const [newEditModel, setNewEditModel] = useState({
    id: generateUUID(),
    title: "",
    name: "",
    surface: "",
    properties: [""],
    images: [],
    isNew: true,
  });
  const [isDragging, setIsDragging] = useState(false);

  // Initialize with server models if provided
  useEffect(() => {
    if (initialModels && initialModels.length > 0) {
      const formattedModels = initialModels.map((model) => ({
        id: model.id || generateUUID(),
        name: model.modelData.name,
        surface: model.modelData.surface,
        properties: model.modelData.properties || [],
        title: model.modelData.title,
        isNew: false,
        images: model.images.map((url) => ({
          url: url,
          isFromServer: true,
        })),
      }));

      setModelsData(formattedModels);
    }
  }, [initialModels]);

  // Notify parent component when models change
  useEffect(() => {
    if (onModelsChange) {
      onModelsChange(modelsData);
    }
  }, [modelsData, onModelsChange]);

  const handleIndexClick = (index) => {
    if (swiper) {
      swiper.slideTo(index);
      setActiveIndex(index);
    }
  };

  const handleSlideChange = () => {
    if (swiper) {
      setActiveIndex(swiper.realIndex);
    }
  };

  const handleSwiperInit = (swiper) => {
    setSwiper(swiper);
  };

  const handlePrevButton = () => {
    if (swiper) {
      swiper.slidePrev();
    }
  };

  const handleNextButton = () => {
    if (swiper) {
      swiper.slideNext();
    }
  };

  const handleDeleteModel = (indexToDelete) => {
    setModelsData((prevModels) => {
      const newModels = [...prevModels];
      newModels.splice(indexToDelete, 1);

      // Reset active index if needed
      if (newModels.length === 0) {
        setActiveIndex(0);
      } else if (activeIndex >= newModels.length) {
        setActiveIndex(newModels.length - 1);
      }

      return newModels;
    });
  };

  const handleOpenDialog = () => {
    setNewModel({
      id: generateUUID(),
      title: "",
      name: "",
      surface: "",
      properties: [""],
      images: [],
      isNew: true,
    });
    setOpenDialog(true);
  };

  const handleOpenDialogEdit = (model) => {
    setNewEditModel(model);
    setOpenDialogEdit(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleCloseDialogEdit = () => setOpenDialogEdit(false);
  const handleAddEditModel = () => {
    if (
      newEditModel.name &&
      newEditModel.surface &&
      newEditModel.images.length > 0
    ) {
      modelsData.map((model) => {
        if (model.id === newEditModel.id) {
          model.name = newEditModel.name;
          model.surface = newEditModel.surface;
          model.properties = newEditModel.properties;
          model.title = newEditModel.title;
          model.images = newEditModel.images;
        }
        return model;
      });

      setOpenDialogEdit(false);
    } else {
      setUploadStatus("error");
      setStatusMessage(
        "Por favor complete todos los campos y agregue al menos una imagen"
      );
      setTimeout(() => {
        setUploadStatus(null);
        setStatusMessage("");
      }, 3000);
    }
  };
  const handleAddModel = () => {
    if (
      newModel.name &&
      newModel.surface &&
      newModel.title &&
      newModel.images.length > 0
    ) {
      setModelsData((prev) => [...prev, newModel]);
      setOpenDialog(false);
    } else {
      setUploadStatus("error");
      setStatusMessage(
        "Por favor complete todos los campos y agregue al menos una imagen"
      );
      setTimeout(() => {
        setUploadStatus(null);
        setStatusMessage("");
      }, 3000);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const newImages = [];

      Array.from(files).forEach((file) => {
        if (file.type.match("image.*")) {
          const imageUrl = URL.createObjectURL(file);
          const uuid = generateUUID();
          newImages.push({
            url: imageUrl,
            file: file,
            uuid: uuid,
            isFromServer: false,
          });
        }
      });

      if (newImages.length > 0) {
        setNewModel((prev) => ({
          ...prev,
          images: [...prev.images, ...newImages],
        }));
      }
    }
  }, []);

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newImages = [];

      Array.from(files).forEach((file) => {
        if (file.type.match("image.*")) {
          const imageUrl = URL.createObjectURL(file);
          const uuid = generateUUID();
          newImages.push({
            url: imageUrl,
            file: file,
            uuid: uuid,
            isFromServer: false,
          });
        }
      });

      if (newImages.length > 0) {
        setNewModel((prev) => ({
          ...prev,
          images: [...prev.images, ...newImages],
        }));
      }
    }
  };

  const handleFileEditInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newImages = [];

      Array.from(files).forEach((file) => {
        if (file.type.match("image.*")) {
          const imageUrl = URL.createObjectURL(file);
          const uuid = generateUUID();
          newImages.push({
            url: imageUrl,
            file: file,
            uuid: uuid,
            isFromServer: false,
          });
        }
      });

      if (newImages.length > 0) {
        setNewEditModel((prev) => ({
          ...prev,
          images: [...prev.images, ...newImages],
        }));
      }
    }
  };

  const handleDeleteImage = (index) => {
    setNewModel((prev) => {
      const newImages = [...prev.images];
      newImages.splice(index, 1);
      return {
        ...prev,
        images: newImages,
      };
    });
  };

  const handleAddProperty = () => {
    setNewModel((prev) => ({
      ...prev,
      properties: [...prev.properties, ""],
    }));
  };

  const handleRemoveProperty = (index) => {
    setNewModel((prev) => {
      const newProperties = [...prev.properties];
      newProperties.splice(index, 1);
      return {
        ...prev,
        properties: newProperties,
      };
    });
  };

  const handlePropertyChange = (index, value) => {
    setNewModel((prev) => {
      const newProperties = [...prev.properties];
      newProperties[index] = value;
      return {
        ...prev,
        properties: newProperties,
      };
    });
  };
  const handlePropertyEditChange = (index, value) => {
    setNewEditModel((prev) => {
      const newProperties = [...prev.properties];
      newProperties[index] = value;
      return {
        ...prev,
        properties: newProperties,
      };
    });
  };

  const handleAddEditProperty = () => {
    setNewEditModel((prev) => ({
      ...prev,
      properties: [...prev.properties, ""],
    }));
  };
  const handleRemoveEditProperty = (index) => {
    setNewEditModel((prev) => {
      const newProperties = [...prev.properties];
      newProperties.splice(index, 1);
      return {
        ...prev,
        properties: newProperties,
      };
    });
  };

  const handleDeleteEditImage = (index) => {
    setNewEditModel((prev) => {
      const newImages = [...prev.images];
      newImages.splice(index, 1);
      return {
        ...prev,
        images: newImages,
      };
    });
  };
  const handleUploadModels = async () => {
    setUploadStatus("loading");
    setStatusMessage("Subiendo modelos...");

    try {
      // Prepare all models for upload
      const uploadItems = modelsData.map((model) => {
        return {
          modelId: model.id,
          images: model.images.map((img, index) => {
            const item = {
              url: img.url,
              file: img.file,
              uuid: img.uuid,
              isFromServer: img.isFromServer,
              index,
            };

            if (!img.isFromServer && img.file) {
              item["originalName"] = img.file.name;
            }

            return item;
          }),
          modelData: {
            name: model.name,
            title: model.title,
            surface: model.surface,
            properties: model.properties,
          },
        };
      });

      // Count new models for status message
      const newModelsCount = modelsData.filter((model) => model.isNew).length;

      // Use custom upload handler if provided
      if (onUpload) {
        await onUpload(uploadItems);
        setUploadStatus("success");
        setStatusMessage(
          newModelsCount > 0
            ? `${newModelsCount} modelos nuevos subidos correctamente`
            : "Modelos procesados correctamente"
        );

        // Mark all models as not new after successful upload
        setModelsData((prevModels) =>
          prevModels.map((model) => ({ ...model, isNew: false }))
        );
      } else {
        // Mock upload process if no custom handler
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Simulate success (80% chance) or error (20% chance)
        const isSuccess = Math.random() > 0.2;

        if (isSuccess) {
          setUploadStatus("success");
          setStatusMessage(
            newModelsCount > 0
              ? `${newModelsCount} modelos nuevos subidos correctamente`
              : "Modelos procesados correctamente"
          );

          // Mark all models as not new
          setModelsData((prevModels) =>
            prevModels.map((model) => ({ ...model, isNew: false }))
          );
        } else {
          throw new Error("Error al subir los modelos");
        }
      }
    } catch (error) {
      setUploadStatus("error");

      setStatusMessage(
        error?.response?.data?.message || "Error al subir los modelos"
      );
    }

    // Clear status after 3 seconds
    setTimeout(() => {
      setUploadStatus(null);
      setStatusMessage("");
    }, 3000);
  };

  return (
    <Stack
      p={{ xs: "25px", md: "40px" }}
      mb="30px"
      bgcolor={theme.palette.background.light}
      borderRadius="10px"
      width={{ lg: "93%", xl: "80%" }}
      sx={{
        margin: "0 auto",
        position: "relative",
      }}
    >
      {modelsData.length > 0 && (
        <>
          <UploadButton
            variant="contained"
            startIcon={
              uploadStatus === "loading" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Upload size={16} />
              )
            }
            onClick={handleUploadModels}
            disabled={uploadStatus === "loading"}
          >
            Subir modelos
          </UploadButton>
          {uploadStatus && (
            <StatusMessage status={uploadStatus}>
              {uploadStatus === "success" && <Check size={16} />}
              {uploadStatus === "error" && <AlertCircle size={16} />}
              {statusMessage}
            </StatusMessage>
          )}
        </>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        display={{ xs: "none", sm: "block" }}
        alignItems={{ xs: "start", md: "center" }}
        gap={2}
      >
        <Typography variant="h3" color="text.secondary">
          MODELOS
        </Typography>
        <Divider
          variant="fullWidth"
          orientation="horizontal"
          sx={{ borderColor: "#a95052", width: "100%" }}
        />
      </Stack>

      {modelsData.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" height="400px">
          <Typography variant="h5" color="text.secondary" mb={3}>
            No hay modelos disponibles
          </Typography>
          <AddModelButton
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={handleOpenDialog}
          >
            Agregar modelo
          </AddModelButton>

          <UploadButton
            variant="contained"
            startIcon={
              uploadStatus === "loading" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Upload size={16} />
              )
            }
            onClick={handleUploadModels}
            disabled={uploadStatus === "loading"}
          >
            Subir modelos
          </UploadButton>
          {uploadStatus && (
            <StatusMessage status={uploadStatus}>
              {uploadStatus === "success" && <Check size={16} />}
              {uploadStatus === "error" && <AlertCircle size={16} />}
              {statusMessage}
            </StatusMessage>
          )}
        </Stack>
      ) : (
        <>
          <Stack
            direction="row"
            pt={{ xs: "0px", sm: "20px" }}
            pl={{ xs: "0px", sm: "20px", md: "40px" }}
            height="100%"
          >
            <Swiper
              grabCursor={true}
              centeredSlides={false}
              slidesPerView={1}
              spaceBetween={10}
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 2,
                slideShadows: false,
              }}
              loop={modelsData.length > 1}
              className="mySwiper"
              onSlideChange={handleSlideChange}
              onSwiper={handleSwiperInit}
              modules={[Navigation, Pagination]}
            >
              {modelsData.map((model, index) => {
                return (
                  <SwiperSlide key={index} height="auto">
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      alignItems={{ xs: "center", md: "start" }}
                      gap={{ xs: 2, md: 10 }}
                      position="relative"
                    >
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteModel(index);
                        }}
                      >
                        <X size={18} />
                      </DeleteButton>

                      <EditButton
                        onClick={(e) => {
                          e.stopPropagation();

                          handleOpenDialogEdit(model);
                        }}
                      >
                        <Pencil size={18} />
                      </EditButton>
                      <Stack width={{ xs: "100%", md: "37%" }}>
                        <Typography variant="h4" color="text.secondary">
                          {model?.title || "Modelo"}
                        </Typography>
                        <Typography variant="h2" color="text.secondary">
                          {model.name}
                        </Typography>
                        <Divider
                          variant="fullWidth"
                          orientation="horizontal"
                          sx={{
                            borderColor: "#a95052",
                            width: "13%",
                            paddingY: "5px",
                          }}
                        />
                        <Typography
                          variant="h4"
                          color="text.secondary"
                          py={{ xs: "10px", md: "20px" }}
                        >
                          Superficie: {model.surface}
                        </Typography>
                        {model.properties?.map((text, idx) => (
                          <Box
                            key={idx}
                            display="flex"
                            pl="20px"
                            pb={{ xs: "0px", md: "12px" }}
                            gap={1}
                            alignItems="center"
                          >
                            <Iconify
                              icon="mdi:check"
                              style={{ color: "white" }}
                            />
                            <Typography variant="h5" color="text.secondary">
                              {text}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                      <Stack direction={{ xs: "column", md: "row" }}>
                        {model.images.length > 0 && (
                          <StyledMinImgBox>
                            <Box
                              component="img"
                              width="100%"
                              height={{ xs: "100%", md: "95%" }}
                              src={model.images[0].url}
                              alt={`slide-${index}`}
                            />
                          </StyledMinImgBox>
                        )}
                        {model.images.length > 1 && (
                          <StyledMinImgBox
                            sx={{
                              display: {
                                xs: "none",
                                md: "block",
                              },
                            }}
                          >
                            <Box
                              component="img"
                              width="100%"
                              height={{ xs: "100%", md: "90%" }}
                              src={model.images[1].url}
                              alt={`slide-${index}-2`}
                            />
                          </StyledMinImgBox>
                        )}
                      </Stack>
                    </Stack>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </Stack>

          <Stack
            direction="row"
            justifyContent="center"
            mt={{ xs: 2, sm: 5, lg: 0, xl: 5 }}
          >
            <Box
              padding="10px"
              borderRadius="50px"
              bgcolor={theme.palette.background.default}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <StyledArrowBox onClick={handlePrevButton}>
                  <Iconify icon="grommet-icons:link-previous" />
                </StyledArrowBox>
                {modelsData
                  ?.slice(0, screenWidth <= 600 ? 10 : modelsData.length)
                  .map((_, index) => (
                    <StyledIndexBox
                      key={index}
                      onClick={() => handleIndexClick(index)}
                      className={activeIndex === index ? "active" : ""}
                    />
                  ))}
                <StyledArrowBox onClick={handleNextButton}>
                  <Iconify icon="ooui:arrow-previous-rtl" />
                </StyledArrowBox>
              </Stack>
            </Box>
          </Stack>

          <Stack alignItems="center" mt={4}>
            <AddModelButton
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleOpenDialog}
            >
              Agregar modelo
            </AddModelButton>
          </Stack>
        </>
      )}

      {/* Add Model Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.background.light,
            color: theme.palette.text.secondary,
          }}
        >
          Agregar nuevo modelo
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.text.secondary,
            }}
          >
            <X />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.background.light, pt: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Stack spacing={2} width={{ xs: "100%", md: "50%" }}>
              <TextField
                label="Titulo"
                fullWidth
                value={newModel.title}
                onChange={(e) =>
                  setNewModel((prev) => ({ ...prev, title: e.target.value }))
                }
                sx={{
                  "& .MuiInputBase-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: theme.palette.text.secondary },
                    "&:hover fieldset": { borderColor: "#a95052" },
                    "&.Mui-focused fieldset": { borderColor: "#a95052" },
                  },
                }}
              />
              <TextField
                label="Nombre del modelo"
                fullWidth
                value={newModel.name}
                onChange={(e) =>
                  setNewModel((prev) => ({ ...prev, name: e.target.value }))
                }
                sx={{
                  "& .MuiInputBase-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: theme.palette.text.secondary },
                    "&:hover fieldset": { borderColor: "#a95052" },
                    "&.Mui-focused fieldset": { borderColor: "#a95052" },
                  },
                }}
              />
              <TextField
                label="Superficie"
                fullWidth
                value={newModel.surface}
                onChange={(e) =>
                  setNewModel((prev) => ({ ...prev, surface: e.target.value }))
                }
                sx={{
                  "& .MuiInputBase-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: theme.palette.text.secondary },
                    "&:hover fieldset": { borderColor: "#a95052" },
                    "&.Mui-focused fieldset": { borderColor: "#a95052" },
                  },
                }}
              />

              <Typography variant="h6" color="text.secondary" mt={2}>
                Propiedades
              </Typography>

              {newModel.properties.map((property, index) => (
                <Stack direction="row" spacing={1} key={index}>
                  <TextField
                    fullWidth
                    value={property}
                    onChange={(e) =>
                      handlePropertyChange(index, e.target.value)
                    }
                    placeholder={`Propiedad ${index + 1}`}
                    sx={{
                      "& .MuiInputBase-root": {
                        color: theme.palette.text.secondary,
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: theme.palette.text.secondary,
                        },
                        "&:hover fieldset": { borderColor: "#a95052" },
                        "&.Mui-focused fieldset": { borderColor: "#a95052" },
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => handleRemoveProperty(index)}
                    sx={{ color: theme.palette.text.secondary }}
                    disabled={newModel.properties.length <= 1}
                  >
                    <X size={20} />
                  </IconButton>
                </Stack>
              ))}

              <MuiButton
                variant="outlined"
                startIcon={<Plus size={16} />}
                onClick={handleAddProperty}
                sx={{
                  borderColor: theme.palette.text.secondary,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: "#a95052",
                    color: "#a95052",
                  },
                }}
              >
                Agregar propiedad
              </MuiButton>
            </Stack>

            <Stack spacing={2} width={{ xs: "100%", md: "50%" }}>
              <Typography variant="h6" color="text.secondary">
                Imágenes del modelo (máximo 2)
              </Typography>

              {newModel.images.length < 2 && (
                <Box
                  component="label"
                  htmlFor="model-image-upload"
                  sx={{ width: "100%", cursor: "pointer" }}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    id="model-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileInputChange}
                  />
                  <DropZone isDragging={isDragging}>
                    <Image size={48} color={theme.palette.text.secondary} />
                    <Typography variant="body1" color="text.secondary" mt={2}>
                      Arrastra y suelta imágenes aquí
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      o haz clic para seleccionar archivos
                    </Typography>
                  </DropZone>
                </Box>
              )}

              {newModel.images.map((image, index) => (
                <Box key={index} position="relative" mb={2}>
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "rgba(169, 80, 82, 0.8)",
                      },
                    }}
                    onClick={() => handleDeleteImage(index)}
                  >
                    <X size={16} />
                  </IconButton>
                  <Box
                    component="img"
                    src={image.url}
                    alt={`Model image ${index + 1}`}
                    sx={{
                      width: "100%",
                      height: "200px",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ bgcolor: theme.palette.background.light, px: 3, pb: 3 }}
        >
          <MuiButton
            onClick={handleCloseDialog}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                color: "#a95052",
              },
            }}
          >
            Cancelar
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleAddModel}
            sx={{
              bgcolor: "#a95052",
              "&:hover": {
                bgcolor: "#8a3f41",
              },
            }}
          >
            Agregar modelo
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Add Edit Model Dialog */}
      <Dialog
        open={openDialogEdit}
        onClose={handleCloseDialogEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.background.light,
            color: theme.palette.text.secondary,
          }}
        >
          Editar modelo
          <IconButton
            aria-label="close"
            onClick={handleCloseDialogEdit}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.text.secondary,
            }}
          >
            <X />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.background.light, pt: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Stack spacing={2} width={{ xs: "100%", md: "50%" }}>
              <TextField
                label="Titulo"
                fullWidth
                value={newEditModel.title}
                onChange={(e) =>
                  setNewEditModel((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiInputBase-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: theme.palette.text.secondary },
                    "&:hover fieldset": { borderColor: "#a95052" },
                    "&.Mui-focused fieldset": { borderColor: "#a95052" },
                  },
                }}
              />
              <TextField
                label="Nombre del modelo"
                fullWidth
                value={newEditModel.name}
                onChange={(e) =>
                  setNewEditModel((prev) => ({ ...prev, name: e.target.value }))
                }
                sx={{
                  "& .MuiInputBase-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: theme.palette.text.secondary },
                    "&:hover fieldset": { borderColor: "#a95052" },
                    "&.Mui-focused fieldset": { borderColor: "#a95052" },
                  },
                }}
              />
              <TextField
                label="Superficie"
                fullWidth
                value={newEditModel.surface}
                onChange={(e) =>
                  setNewEditModel((prev) => ({
                    ...prev,
                    surface: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiInputBase-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: theme.palette.text.secondary },
                    "&:hover fieldset": { borderColor: "#a95052" },
                    "&.Mui-focused fieldset": { borderColor: "#a95052" },
                  },
                }}
              />

              <Typography variant="h6" color="text.secondary" mt={2}>
                Propiedades
              </Typography>

              {newEditModel.properties.map((property, index) => (
                <Stack direction="row" spacing={1} key={index}>
                  <TextField
                    fullWidth
                    value={property}
                    onChange={(e) =>
                      handlePropertyEditChange(index, e.target.value)
                    }
                    placeholder={`Propiedad ${index + 1}`}
                    sx={{
                      "& .MuiInputBase-root": {
                        color: theme.palette.text.secondary,
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: theme.palette.text.secondary,
                        },
                        "&:hover fieldset": { borderColor: "#a95052" },
                        "&.Mui-focused fieldset": { borderColor: "#a95052" },
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => handleRemoveEditProperty(index)}
                    sx={{ color: theme.palette.text.secondary }}
                    disabled={newEditModel.properties.length <= 1}
                  >
                    <X size={20} />
                  </IconButton>
                </Stack>
              ))}

              <MuiButton
                variant="outlined"
                startIcon={<Plus size={16} />}
                onClick={handleAddEditProperty}
                sx={{
                  borderColor: theme.palette.text.secondary,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: "#a95052",
                    color: "#a95052",
                  },
                }}
              >
                Agregar propiedad
              </MuiButton>
            </Stack>

            <Stack spacing={2} width={{ xs: "100%", md: "50%" }}>
              <Typography variant="h6" color="text.secondary">
                Imágenes del modelo (máximo 2)
              </Typography>

              {newEditModel.images.length < 2 && (
                <Box
                  component="label"
                  htmlFor="model-image-upload"
                  sx={{ width: "100%", cursor: "pointer" }}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    id="model-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileEditInputChange}
                  />
                  <DropZone isDragging={isDragging}>
                    <Image size={48} color={theme.palette.text.secondary} />
                    <Typography variant="body1" color="text.secondary" mt={2}>
                      Arrastra y suelta imágenes aquí
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      o haz clic para seleccionar archivos
                    </Typography>
                  </DropZone>
                </Box>
              )}

              {newEditModel.images.map((image, index) => (
                <Box key={index} position="relative" mb={2}>
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "rgba(169, 80, 82, 0.8)",
                      },
                    }}
                    onClick={() => handleDeleteEditImage(index)}
                  >
                    <X size={16} />
                  </IconButton>
                  <Box
                    component="img"
                    src={image.url}
                    alt={`Model image ${index + 1}`}
                    sx={{
                      width: "100%",
                      height: "200px",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ bgcolor: theme.palette.background.light, px: 3, pb: 3 }}
        >
          <MuiButton
            onClick={handleCloseDialogEdit}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                color: "#a95052",
              },
            }}
          >
            Cancelar
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleAddEditModel}
            sx={{
              bgcolor: "#a95052",
              "&:hover": {
                bgcolor: "#8a3f41",
              },
            }}
          >
            Modificar modelo
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ModelsCarouselEdit;
